/**
 * Seite „Zeitstrahl · Transport" - Portierung von `renderTimelinePage` (`index.html:13733-13798`).
 *
 * Klassennamen, Reihenfolge, Texte, Filter- und Sortierweg sind woertlich uebernommen; ergaenzt
 * wurden Typen, der Umbau der DB-Bindung auf Props und die eine bewusste Verhaltensaenderung.
 *
 * ================= Der Umbau: globales DB -> Props, Modul-Globals -> useState =================
 *
 * Das Original liest `DB.orders` (ueber `activeReportingOrders()`), schreibt sein Ergebnis in
 * `#page-timeline` und haelt Suchbegriff und Sortierrichtung in den Modul-Globals `timelineSearch`
 * und `timelineSortDirection` (`setTimelineSearch`/`setTimelineSortDirection`, index.html:13699-13732).
 * Hier kommen die Daten als Prop (`bestellungen`), die Statusdefinitionen als `definitionen`,
 * `openDrawer(id)` wird zu `onOeffnen(id)`, und die beiden Oberflaechenzustaende liegen im
 * `useState` dieser Komponente - dieselbe Linie, die `lib/status.ts` fuer
 * `COLUMN_FILTERS`/`COLUMN_FILTER_OPEN` gezogen hat (Oberflaechenzustand gehoert nicht nach `lib/`).
 *
 * ================= Verhaltensaenderung 6 (die Darstellung steht, seit „Sammelroute fuer die
 * Zeitstrahl-Liste" (25.08.2026) hat sie auch wieder eine Quelle) =================
 *
 * Bei verknuepfter Sendung kommen Fortschritt und Datumsangaben aus den echten Stationsdaten statt
 * aus dem Board-Status; die Sendungs-ETA hat Vorrang vor der Bestell-ETA, und die Zeile weist ihre
 * Quelle sichtbar aus (`.tlp-quelle-dsv`). Diese Darstellung ist VOLLSTAENDIG GEBAUT und bleibt
 * unangetastet (`spurfortschritt`/`Stationsspur`, `./Stationsspur.tsx`) - zwischen dem Auftrag
 * „Sendungsabruf beim App-Start entfernen" (2026-08-22) und diesem hier fehlte ihr eine QUELLE: die
 * Seite bekam die verknuepfte Sendung damals ueber eine (in Produktion IMMER leere, siehe
 * Kopfkommentar `api/sendungen.ts`) Liste aus dem Store und loeste ueber das nie gesetzte
 * `order.shipmentId` auf; beides war entfallen, samt der Aufloesefunktion (`lib/suche.ts`), und die
 * Seite reichte fuer JEDE Zeile `sendung: null` durch.
 *
 * Seither liefert `POST /api/orders/shipments` (neu, `backend/src/http/routes/orderShipments.ts`)
 * die verknuepfte Sendung je Bestellung GEBUENDELT fuer eine ganze Liste - `App.tsx` laedt sie fuer
 * die aktuell sichtbaren Bestellungen dieser Seite und reicht sie als `sendungenNachBestellung`
 * herein (`orderId -> Sendung | null`, KEINE Liste zum Durchsuchen - `zeitstrahlTrifftSuche`
 * erwartet die BEREITS AUFGELOESTE Sendung genau dieser einen Bestellung, siehe `lib/suche.ts`).
 * Jede Zeile loest ihre Sendung unten aus dieser Prop auf, statt sie hart auf `null` zu setzen.
 *
 * NICHT TEIL DIESES AUFTRAGS: die Sammelroute liefert `SendungsTreffer` (die Serverform, siehe
 * `lib/types.ts`) - keine Stationsdaten. `App.tsx` bildet daraus die hier erwartete `Sendung`-Form,
 * `stationen`/`zoll` bleiben dabei unbesetzt (`undefined`); wie eine Stufe aus rohen DSV-Ereignissen
 * berechnet wird ("station vs eventCode"), ist Sache des separaten Zweigs
 * `feat/zeitstrahl-eventcode-mapping` und wird mit dessen Merge nachgezogen, nicht hier.
 *
 * Diese Flaeche ist deshalb von der Screenshot-Baseline ausgenommen (`test/visual/ausnahmen.ts`,
 * Eintrag `TimelinePage`); der Nachweis laeuft vollstaendig ueber
 * `test/komponenten/TimelinePage/zeitstrahl.test.tsx`.
 *
 * ================= Auftrag 2: dreispaltige Zeile, Artikelstreifen, Positions-Popup =================
 *
 * `.tlp-row`/`.tlp-grid-head` bekommen eine dritte Spalte (Bestellinfo | Artikelstreifen |
 * Stufenspur) - die Spaltenbreiten selbst stehen NICHT hier in `bestand.css`, sondern in
 * `styles/neubau/zeitstrahlZeile.css` (Kopfkommentar dort begruendet, warum: dieselbe Regel wie bei
 * `styles/neubau/artikelbild.css`, `bestand.css` muss zeichengleich zum `<style>`-Block aus
 * `index.html` bleiben, `test/reference/stylesheet.test.ts`). Der linke Block `.tlp-order` bekommt
 * eine vierte Zeile (Lieferant), dazu neu das Bestelldatum neben der Bestellnummer und die
 * Positions-/Stueckzahl in `.tlp-order-meta` - alle drei ohne Vorbild, alle drei ebenfalls in
 * `zeitstrahlZeile.css`. Der Artikelstreifen selbst (`Artikelstreifen.tsx`) haelt sein Popup
 * (`PositionenPopup.tsx`) und dessen Oeffnen-Zustand selbst; diese Seite reicht nur die Bestellung
 * durch.
 *
 * ================= Woertlich uebernommene Eigenheiten (gemeldet, nicht repariert) =================
 *
 * 1. Die Sortieroptionen heissen „Aufsteigend · 1 → 8" und „Absteigend · 8 → 1"
 *    (`index.html:13772-13773`), die Legende darunter zaehlt aber nur bis 6 - `SHIP_STAGES` hat
 *    sechs Stufen, seit die beiden frueheren Transport-Spalten ausgeblendet wurden (Kommentar in
 *    `orderStageIndex`, `index.html:13694-13695`). Die „8" ist ein Rest aus der Zeit davor.
 *    Woertlich uebernommen.
 * 2. Die Sortierung dreht ueber `direction` NUR den Stufenvergleich um, nicht den ETA-Vergleich
 *    dahinter (`index.html:13740`). Bei gleicher Stufe steht die frueheste ETA deshalb in BEIDEN
 *    Richtungen vorn. Woertlich uebernommen.
 * 3. `.tlp-node`/`.tlp-conn` tragen ihren Zusatz ueber eine Zeichenkette mit Leerzeichen
 *    (`class="tlp-conn "` ohne Zusatz). Hier gleich gebaut, damit das Klassenattribut Zeichen fuer
 *    Zeichen dasselbe ist wie im Bestand.
 * 4. Die leere Seite unterscheidet zwei Faelle: mit Suchbegriff „Keine passende Bestellung
 *    gefunden.", ohne „Keine aktiven Bestellungen." (`index.html:13796`). NEUBAU dazu: ohne
 *    Suchbegriff bei eingeschaltetem Schalter „Neu (n)" steht „Keine Bestellung mit neuen Artikeln."
 *    (die Seite hat aktive Bestellungen, nur keine mit neuen Artikeln); der Suchbegriff geht vor.
 *
 * ================= Auftrag "DSV-Badge in der Zeitstrahl-Sendungsuebersicht" =================
 *
 * Neu in `.tlp-order-meta`: ein Icon-Badge (`.tlp-badge-dsv`, lucide `truck`, Label „DSV"), sichtbar
 * NUR wenn `order.dsvVerknuepft === true` (Backend-EXISTS-Subquery gegen `order_shipments`,
 * `backend/src/domaene/orders.ts`) - EIN EIGENSTAENDIGES Signal, unabhaengig von `.tlp-quelle-dsv`
 * direkt darueber (das bleibt UNVERAENDERT, siehe „Verhaltensaenderung 6" oben: es haengt an
 * `fortschritt.quelle`, also an bereits geladenen UND auswertbaren Stationsdaten, waehrend das Badge
 * schon bei einer blossen Verknuepfung erscheint, auch ohne dass `sendungenNachBestellung` fuer die
 * Zeile einen Treffer traegt). `sendung.trackingNo` (falls geladen) steht als `title`-Tooltip auf dem
 * Badge, ist aber NICHT Bedingung fuer dessen Anzeige. Die Klasse selbst steht in
 * `styles/neubau/zeitstrahlZeile.css` (nicht `bestand.css`), aus demselben Grund wie jede andere
 * neue Regel dort (siehe „Auftrag 2" oben).
 *
 * ================= Was NICHT hierher gehoert =================
 *
 * Die Seite ist rein lesend: kein Nachladen, kein Zugriff auf `Speicher`. Wer sie einhaengt, reicht
 * Bestellungen aus dem Speicher herein (siehe Task-Bericht, „Einhaengepunkte").
 */
import { Fragment, useState } from "react";
import { STATUS_ICONS } from "../../config/board.ts";
import { SHIP_STAGES } from "../../config/shipping.ts";
import { fmtDate, normSearch } from "../../lib/format.ts";
import { kennungVon } from "../../lib/bestellkennung.ts";
import { etaMitQuelle } from "../../lib/bestandQuellen.ts";
import { istBestellungNachLaenderFilterSichtbar, LAENDERFILTER_VORGABE } from "../../lib/laender.ts";
import {
  istBestellungNachTransportartFilterSichtbar,
  TRANSPORTARTFILTER_VORGABE,
} from "../../lib/transportart.ts";
import { formatPositionenZusammenfassung } from "../../lib/positionenSumme.ts";
import { activeReportingOrders, type StatusDefinitionen } from "../../lib/status.ts";
import { zeitstrahlTrifftSuche } from "../../lib/suche.ts";
import { supplierBadgeUrl } from "../../lib/suppliers.ts";
import type { Order, OrderItem, Sendung, Supplier } from "../../lib/types.ts";
import type { LaenderFilter, TransportartFilter } from "../../state/arbeitsplatz.ts";
import { useAnsicht } from "../../state/useAnsicht.ts";
import { useEntprelltenAdresswert } from "../../state/useEntprelltenAdresswert.ts";
import { Ikone } from "../Ikone.tsx";
import { Seitenkopf } from "../App/Seitenkopf.tsx";
import { istEinfacherKlick, Verweis } from "../Verweis.tsx";
import { BestellungSplitMarke } from "../SplitMarke.tsx";
import { hatNeueArtikel, NeuerArtikelMarke, NeuFilterSchalter } from "../NeuerArtikelMarke.tsx";
import { Artikelstreifen } from "./Artikelstreifen.tsx";
import {
  ETA_ETD_LABEL_UEBERSCHREIBUNG,
  Stationsspur,
  spurfortschritt,
  type Spurfortschritt,
} from "./Stationsspur.tsx";
import { langeAngeliefert } from "./anlieferung.ts";
import { ZeitstrahlSuche } from "./ZeitstrahlSuche.tsx";
import { ZollStufe } from "./ZollStufe.tsx";

export interface ZeitstrahlSeiteProps {
  bestellungen: readonly Order[];
  /**
   * NEU seit dem Auftrag „EU/China-Laenderfilter in die App-Kopfzeile verschieben": bis dahin bekam
   * diese Seite GAR KEIN `lieferanten`-Prop (sie brauchte es nicht) - der Laenderfilter braucht die
   * Lieferantenliste aber, um `order.supplier`/`order.supplierId` auf eine Region aufzuloesen (siehe
   * `laenderFilter` unten). `App.tsx` reicht `zustand.lieferanten` seither auch hierher durch.
   */
  lieferanten?: readonly Supplier[] | null | undefined;
  definitionen: StatusDefinitionen;
  onOeffnen: (bestellungId: string) => void;
  /**
   * Die verknuepfte Sendung je Bestellung (`orderId -> Sendung | null`) - siehe Kopfkommentar,
   * Abschnitt "Verhaltensaenderung 6". Ein fehlender Schluessel zaehlt wie `null` (noch nicht
   * geladen bzw. keine Verknuepfung); `App.tsx` befuellt sie ueber die neue Sammelroute.
   */
  sendungenNachBestellung: Record<string, Sendung | null>;
  /**
   * Der EU/China-Laenderfilter (app-weiter Zustand aus `App.tsx`) - wendet
   * `lib/laender.ts#istBestellungNachLaenderFilterSichtbar` auf `reportingOrders` an, GENAU an der
   * Stelle, an der diese Seite ihre anzuzeigende Bestellliste bildet (index.html:13736-13740).
   *
   * PFLICHT seit der Pruefrunde 2 (Befund W1) - siehe `Board/BoardSeite.tsx#BoardSeiteProps`,
   * Kopfkommentar am selben Feld, fuer die ausfuehrliche Begruendung.
   */
  laenderFilter: LaenderFilter;
  /**
   * Der Luftfracht/Seefracht-Filter (Auftrag „Luftfracht/Seefracht-Filter im Bestellhub",
   * 2026-08-27, app-weiter Zustand aus `App.tsx`) - wendet
   * `lib/transportart.ts#istBestellungNachTransportartFilterSichtbar` an derselben Stelle wie
   * `laenderFilter` an.
   *
   * PFLICHT seit der Nachbesserung zum Transportart-Filter (nach Commit c9da74e) - siehe `Board/BoardSeite.tsx#BoardSeiteProps`,
   * Kopfkommentar am selben Feld, fuer die ausfuehrliche Begruendung (derselbe Befund wie W1 bei
   * `laenderFilter`, hier auf den Transportart-Filter uebertragen).
   */
  transportartFilter: TransportartFilter;
}

/** Sortierrichtung; im Original das Modul-Global `timelineSortDirection` (index.html:13699-13702). */
export type Sortierrichtung = "asc" | "desc";

/** Eine fertig aufbereitete Zeile: Bestellung, ihre Sendung, ihr Fortschritt und ihre ETA. */
interface Zeitstrahlzeile {
  order: Order;
  sendung: Sendung | null;
  fortschritt: Spurfortschritt;
  /** Die massgebliche ETA: die der Sendung, sonst die der Bestellung (Verhaltensaenderung 6). */
  eta: string;
}

const ikonen = STATUS_ICONS as unknown as Record<string, string>;

export function ZeitstrahlSeite({
  bestellungen,
  lieferanten,
  definitionen,
  onOeffnen,
  sendungenNachBestellung,
  laenderFilter = LAENDERFILTER_VORGABE,
  transportartFilter = TRANSPORTARTFILTER_VORGABE,
}: ZeitstrahlSeiteProps) {
  // Suche und Sortierrichtung leben in der ADRESSE (Spezifikation § 3.3, Aufgabe 4).
  const { ansicht, setzeAnsicht } = useAnsicht();
  const { q: suchbegriff, dir: sortierrichtung, neu } = ansicht.zeitstrahl;
  // FUNKTIONSFORM statt eines fertigen Objekts - derselbe Befund wie in `BoardSeite.tsx`/
  // `LieferantenSeite.tsx`: der verzoegerte Suchschreiber unten darf beim Feuern nicht auf einem
  // inzwischen veralteten `ansicht.zeitstrahl` schliessen und eine zwischenzeitliche Sortierrichtung
  // wieder zuruecksetzen.
  const setzeZeitstrahl = (teil: Partial<typeof ansicht.zeitstrahl>): void =>
    setzeAnsicht((alt) => ({ zeitstrahl: { ...alt.zeitstrahl, ...teil } }), { ersetzen: true });
  const setSortierrichtung = (wert: Sortierrichtung): void => setzeZeitstrahl({ dir: wert });
  const setzeNeu = (an: boolean): void => setzeZeitstrahl({ neu: an });

  // Der Sofortwert fuers Eingabefeld; die Adresse zieht 300 ms spaeter nach (`useEntprelltenAdresswert`).
  const [suchbegriffRoh, setSuchbegriffRoh] = useEntprelltenAdresswert(suchbegriff, (wert) =>
    setzeZeitstrahl({ q: wert }),
  );

  // index.html:13736-13740, danach der Laenderfilter (siehe Kopfkommentar am Prop oben) - dieselbe
  // geteilte Regel wie in `BoardSeite.tsx`/`UebersichtSeite.tsx`/`ZahlungsSeite.tsx`/`KostenSeite.tsx`.
  const direction = sortierrichtung === "desc" ? -1 : 1;
  const reportingOrders = activeReportingOrders(bestellungen, definitionen)
    .filter((order) => istBestellungNachLaenderFilterSichtbar(order, lieferanten, laenderFilter))
    .filter((order) => istBestellungNachTransportartFilterSichtbar(order, transportartFilter));
  const heute = new Date();
  // Je Zeile aus der Prop aufgeloest (siehe Kopfkommentar, "Verhaltensaenderung 6") - ein fehlender
  // Schluessel zaehlt wie `null` (Board-Rueckfall), genau wie eine Bestellung ohne Verknuepfung.
  // Dieselbe aufgeloeste Sendung geht in Filter UND Aufbereitung, da beide dieselbe Sendung
  // derselben Zeile meinen. `suchbegriffRoh`, nicht `suchbegriff`: nur der Schreiber in die Adresse
  // ist entprellt (§ 4), die sichtbare Liste soll bei jedem Tastendruck sofort nachziehen (derselbe
  // Befund wie in `BoardSeite.tsx`, dort mit Messung).
  const zeilenVorNeuFilter: Zeitstrahlzeile[] = reportingOrders
    .filter((order) =>
      zeitstrahlTrifftSuche(order, suchbegriffRoh, sendungenNachBestellung[String(order.id)] ?? null),
    )
    .map((order) => {
      const sendung = sendungenNachBestellung[String(order.id)] ?? null;
      return {
        order,
        sendung,
        fortschritt: spurfortschritt(order, definitionen, sendung),
        // Die Regel (die ETA der Sendung, sonst die der Bestellung) steht in `etaMitQuelle`; der Server der KI-Werkzeuge ruft dieselbe.
        eta: etaMitQuelle(sendung, order).eta ?? "",
      };
    })
    // Folie 10: lange angelieferte Bestellungen entfallen; ein Suchbegriff hebt das auf (`anlieferung.ts`).
    .filter((z) => !!normSearch(suchbegriffRoh) || !langeAngeliefert(z.order, z.fortschritt, definitionen, heute));
  // NEUBAU, Feature "Neue Artikel an Bestellungen markieren und filtern" (Task 6): der Schalter "Neu (n)".
  // Dieser Schritt ist der LETZTE Filter vor dem Sortieren; was die Seite sonst noch ausblendet (Zustand,
  // Laender, Transportart, Suche, lange Angelieferte - und jeder kuenftige Schritt hinter `.map`), steht in
  // `zeilenVorNeuFilter` und muss VOR der Zaehlung liegen: die Zahl im Schalter ist die Zahl, die er bei Klick
  // zeigen wuerde (Spec E6). Eingeschaltet ohne Treffer zeigt der Leerzustand (unten) „Keine Bestellung mit neuen Artikeln.", der Schalter `Neu (0)`.
  const neuAnzahl = zeilenVorNeuFilter.filter((zeile) => hatNeueArtikel(zeile.order)).length;
  const zeilen: Zeitstrahlzeile[] = zeilenVorNeuFilter
    .filter((zeile) => !neu || hatNeueArtikel(zeile.order))
    .sort(
      (a, b) =>
        direction * (a.fortschritt.erreicht - b.fortschritt.erreicht) ||
        (a.eta || "9999").localeCompare(b.eta || "9999"),
    );

  return (
    <div className="ov-wrap">
      <Seitenkopf titel="Zeitstrahl" unterzeile="Transportverlauf aller Sendungen">
        <div className="tlp-controls">
          {/* Schalter "Neu (n)": links neben dem Suchfeld, dieselbe Komponente wie im Board
              (`NeuFilterSchalter`, `NeuerArtikelMarke.tsx`). */}
          <NeuFilterSchalter an={neu} anzahl={neuAnzahl} onUmschalten={setzeNeu} />
          <ZeitstrahlSuche wert={suchbegriffRoh} onAendern={setSuchbegriffRoh} />
          <div className="tlp-sort">
            <label>
              Sortierung
              <select
                aria-label="Bestellungen nach Fortschritt sortieren"
                value={sortierrichtung}
                onChange={(ereignis) =>
                  // index.html:13700 - jeder Wert ausser 'desc' bedeutet 'asc'.
                  setSortierrichtung(ereignis.target.value === "desc" ? "desc" : "asc")
                }
              >
                <option value="asc">Aufsteigend · 1 → 8</option>
                <option value="desc">Absteigend · 8 → 1</option>
              </select>
            </label>
          </div>
        </div>
      </Seitenkopf>
      {/*
        index.html:13779 - die Stufen mit " → " verbunden. Auftrag "Uebersichtsseite Zeitstrahl -
        Hafen DE zu ETA und See zu ETD" (Asana-Ticket 1218051183819571, 2026-09-01): die Kopfzeile
        zeigt UNBEDINGT "ETD"/"ETA" statt "See"/"Hafen DE" - unabhaengig von der Transportart der
        Zeilen darunter, siehe Kopfkommentar `Stationsspur.tsx`. Das Zeilenverhalten selbst bleibt
        unveraendert (dort nur bei `transportMode === "air"`).
      */}
      <div className="tlp-legend">
        {SHIP_STAGES.map((s, i) => (
          <Fragment key={s.id}>
            {i > 0 ? " → " : null}
            <span>
              <b>{i + 1}</b> {ETA_ETD_LABEL_UEBERSCHREIBUNG.get(s.id) ?? s.label}
            </span>
          </Fragment>
        ))}
      </div>
      {zeilen.length ? (
        <>
          {/* index.html:13781 */}
          <div className="tlp-grid-head">
            <div />
            {/*
              Auftrag 2: die dritte, ebenfalls leere Zelle ueber dem Artikelstreifen - ohne sie
              rutschte die Beschriftung darunter (`.tlp-cols`) in die MITTLERE Spalte statt in die
              dritte, in der die Zeilen ihre `Stationsspur` tatsaechlich zeichnen (siehe
              `zeitstrahlZeile.css`, `.tlp-cols { grid-column: 3; }`, fuer den Fall, dass eine Zeile
              gar keinen Artikelstreifen hat und die mittlere Spalte in IHR leer bleibt).
            */}
            <div />
            <div className="tlp-cols">
              {/* Auftrag "Uebersichtsseite Zeitstrahl - Hafen DE zu ETA und See zu ETD" (siehe
                  `.tlp-legend` oben) - dieselbe UNBEDINGTE Ueberschreibung fuer die Spaltenkoepfe. */}
              {SHIP_STAGES.map((s) => (
                <div className="tlp-col-lbl" key={s.id}>
                  {ETA_ETD_LABEL_UEBERSCHREIBUNG.get(s.id) ?? s.label}
                </div>
              ))}
            </div>
          </div>
          <div className="tlp-orders">
            {zeilen.map(({ order, sendung, fortschritt, eta }) => {
              const st = definitionen.STATUS_MAP[order.status as string];
              const etaTxt = eta ? `ETA ${fmtDate(eta)}` : "ETA offen";
              const bestellungId = order.id as string;
              const mengeText = formatPositionenZusammenfassung(order.items as OrderItem[] | undefined);
              return (
                <Verweis
                  key={bestellungId}
                  className="tieflink-anker"
                  replace
                  // Befund 1 der Pruefrunde 4 (Fixrunde 4): `onClick` sitzt auf dem Anker, nicht auf
                  // dem inneren `<div>` - dasselbe Muster wie an den fuenf Knopf-Stellen (Fixrunde 3,
                  // Kopfkommentar am `<Verweis>` in Handlungsbedarf.tsx). Das `<div>` war NIE
                  // fokussierbar - ein Tastaturnutzer erreichte `onOeffnen` (und damit
                  // `versende({art:"geoeffnet"})`, die SSE-Konflikterkennung) hier seit jeher nicht,
                  // unabhaengig von Aufgabe 5: erst der Anker macht die Flaeche ueberhaupt
                  // tastaturfaehig und legt die Luecke offen.
                  onClick={(ereignis) => { if (istEinfacherKlick(ereignis)) onOeffnen(bestellungId); }}
                  ziel={{
                    ...ansicht,
                    bestellung: {
                      kennung: kennungVon(order, bestellungId),
                      tab: "main",
                      aufgabe: null,
                      dokument: null,
                    },
                  }}
                >
                  <div className="tlp-row">
                    <div className="tlp-order">
                      {/* Zeile 1 (Auftrag 2): Bestellnummer, rechts daneben das Bestelldatum. */}
                      <div className="tlp-order-kopf">
                        <div className="tlp-order-no mono">
                          {(order.orderNo as string) || "—"}
                          {/* Neue Artikel: Marke "Neu" vor der Split-Marke, wie im Board (`NeuerArtikelMarke.tsx`). */}
                          <NeuerArtikelMarke order={order} />
                          {/* Nicht klickbar: der Anker (`display: contents`) umschliesst die ganze Zeile - ein Knopf darin waere
                             ungueltiges HTML, ein Geschwister ausserhalb laege ausserhalb des Zeilenkastens. Der Zeilenklick
                             oeffnet die Bestellung, der Drawer-Kopf verweist auf die Quelle. */}
                          <BestellungSplitMarke order={order} klickbar={false} />
                        </div>
                        <div className="tlp-order-datum">
                          {order.orderDate ? fmtDate(order.orderDate as string) : ""}
                        </div>
                      </div>
                      <div className="tlp-order-name">{(order.name as string) || "Bestellung"}</div>
                      {/*
                        Zeile 3 (Auftrag 2, NEU): Lieferanten-Abzeichen + Firmenname. Ohne
                        `order.supplier` entfaellt die ganze Zeile (Auftragstext).
                      */}
                      {order.supplier ? (
                        <div className="tlp-order-lieferant">
                          <img
                            className="tlp-order-lieferant-abzeichen"
                            src={supplierBadgeUrl(order.supplier)}
                            alt=""
                            aria-hidden="true"
                          />
                          <span className="tlp-order-lieferant-name">{String(order.supplier)}</span>
                        </div>
                      ) : null}
                      <div className="tlp-order-meta">
                        {st ? (
                          <span className="status-pill" style={{ background: st.soft, color: st.color }}>
                            {/*
                              Task 6 (Verdrahtungsplan Phase 6): `<i data-lucide="…">` -> `Ikone`, eine
                              der vier Fundstellen im ganzen `src/`, die niemand hydriert (siehe
                              Kopfkommentar der Datei).

                              `icon(st.iconSvg || STATUS_ICONS[st.icon] || 'list', 'status-icon')`
                              (index.html:13791). Trotz des Feldnamens traegt `st.iconSvg` schon im
                              BESTAND keinen Roh-SVG-Text, sondern denselben NAMEN wie
                              `STATUS_ICONS[st.icon]` (index.html:4150: `iconSvg: STATUS_ICONS[icon]`) -
                              `setStatusDefinitions` (`lib/status.ts`) setzt `iconSvg` NACHDEM `icon`
                              bereits auf einen bekannten Schluessel abgesichert wurde (Vorgabe
                              `"list"`); beide Operanden sind fuer jedes durchgereichte `st` deshalb
                              immer identisch. `Ikone` erhaelt hier nur noch `ikonen[st.icon] || "list"`
                              als NAME - der `st.iconSvg`-Zweig entfaellt ersatzlos: nicht nur, weil er
                              redundant waere, sondern weil `Ikone` ohnehin ausschliesslich einen
                              Symbolnamen entgegennimmt und kein Roh-SVG einbetten kann (`Ikone.tsx:290`,
                              unbekannter Name -> `null`).
                            */}
                            <Ikone name={ikonen[st.icon] || "list"} className="status-icon" />
                            {st.label}
                          </span>
                        ) : (
                          ""
                        )}
                        {fortschritt.quelle === "dsv" ? (
                          <span
                            className="tlp-quelle-dsv"
                            title="Stationsdaten der verknüpften Sendung"
                          >
                            {(sendung?.trackingNo as string) || "Sendung"}
                          </span>
                        ) : null}
                        {/*
                          Auftrag "DSV-Badge in der Zeitstrahl-Sendungsuebersicht": ein EIGENSTAENDIGES
                          Signal, unabhaengig von `.tlp-quelle-dsv` direkt darueber (das haengt an
                          `fortschritt.quelle`, also an bereits GELADENEN und AUSWERTBAREN
                          Stationsdaten). `order.dsvVerknuepft` kommt aus der Bestellliste selbst
                          (Backend-EXISTS-Subquery gegen `order_shipments`, `domaene/orders.ts`) und ist
                          schon dann `true`, wenn eine Sendung verknuepft ist - VOR und UNABHAENGIG
                          davon, ob `sendungenNachBestellung` fuer diese Zeile ueberhaupt einen Eintrag
                          traegt. Zeilen ohne Verknuepfung bekommen KEIN gedaempftes Gegenstueck
                          (Auftragstext) - deshalb `null`, kein Platzhalter-Badge.
                        */}
                        {order.dsvVerknuepft === true ? (
                          <span
                            className="status-pill tlp-badge-dsv"
                            title={(sendung?.trackingNo as string) || undefined}
                          >
                            <Ikone name="truck" className="status-icon" />
                            DSV
                          </span>
                        ) : order.transportMode === "air" ? (
                          // Feature "See/Flug-Umschaltung" (Spec E6, Ruling 2026-08-26): Geschwister
                          // von .tlp-badge-dsv direkt darueber, NICHT ein Umbau von .tlp-quelle-dsv
                          // (die bleibt laut Kopfkommentar der Komponente unangetastet). dsvVerknuepft
                          // hat Vorrang - der Zustand "beides wahr" ist serverseitig gesperrt
                          // (TransportModeGesperrtError), die Oberflaeche bleibt trotzdem defensiv.
                          <span
                            className="status-pill tlp-badge-air"
                            title={
                              [order.airCarrier, order.airAwbNo].filter(Boolean).join(" · ") ||
                              undefined
                            }
                          >
                            <Ikone name="plane" className="status-icon" />
                            Flug
                          </span>
                        ) : order.transportMode === "road" ? (
                          // DRITTE Transportart "road" (Asana 1218176179911324): weiteres Geschwister
                          // von .tlp-badge-dsv/.tlp-badge-air, gleicher Bauplan wie das Flug-Badge
                          // direkt darueber. Bewusste Doppelnutzung des `truck`-Icons: .tlp-badge-dsv
                          // fuehrt es bereits (siehe oben) - die beiden Badges schliessen einander aus
                          // (dsvVerknuepft hat Vorrang) und der Text unterscheidet sie, deshalb kein
                          // zweites Icon. Damit sie trotzdem auf einen Blick auseinanderzuhalten sind,
                          // traegt .tlp-badge-road eine EIGENE Akzentfarbe (neubau/zeitstrahlZeile.css).
                          <span
                            className="status-pill tlp-badge-road"
                            title={
                              [order.roadCarrier, order.roadCmrNo].filter(Boolean).join(" · ") ||
                              undefined
                            }
                          >
                            <Ikone name="truck" className="status-icon" />
                            Straße
                          </span>
                        ) : null}
                        <span className="tlp-eta">{etaTxt}</span>
                        {/* Zeile 4, NEU (Auftrag 2): Positions-/Stueckzahl, z. B. "7 Pos · 1.240 Stk". Bei
                            null Positionen entfaellt die Angabe (Auftragstext). */}
                        {mengeText ? <span className="tlp-order-menge">{mengeText}</span> : null}
                      </div>
                      {sendung?.zoll ? <ZollStufe zoll={sendung.zoll} /> : null}
                    </div>
                    <Artikelstreifen order={order} />
                    {/*
                      Auftrag "Zeitstrahl-Uebersicht ETD/ETA-Labels und Datumsquelle fuer
                      Flug-Sendungen" (28.08.2026), um "road" erweitert (Asana 1218176179911324):
                      `transportMode` steuert NUR die Beschriftung der Knoten "See"/"Hafen DE" ->
                      "ETD"/"ETA" (Flug) bzw. "See" -> "Abfahrt" (Straße) (Kopfkommentar
                      `Stationsspur.tsx`, Punkt 1 bzw. Abschnitt "DRITTE Transportart road") - die
                      Datumsquelle selbst (Punkt 2, `air_etd`/`air_eta`/`air_absina` bzw.
                      `road_etd`/`road_eta`) greift bereits in `spurfortschritt()` oben, unabhaengig
                      von dieser Prop.
                    */}
                    <Stationsspur fortschritt={fortschritt} transportMode={order.transportMode} />
                  </div>
                </Verweis>
              );
            })}
          </div>
        </>
      ) : (
        <div className="empty-note">
          {suchbegriffRoh
            ? "Keine passende Bestellung gefunden."
            : neu
              ? "Keine Bestellung mit neuen Artikeln."
              : "Keine aktiven Bestellungen."}
        </div>
      )}
    </div>
  );
}
